import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { IAgent } from '../types/agent';
import {
  fetchAgentList,
  fetchAgentBrandList,
  fetchBrandList,
} from '../hooks/queries';

import TableWithSearchCTA from '../components/TableWithSearchCTA';
import Modal from '../components/common/Modal';
import { BtnPrimary } from '../components/common/Buttons';
import AddAgent from '../components/forms/AddAgent';
import ChangePassword from '../components/forms/ChangePassword';
import { useAgentColumns } from '../components/columns/useAgentCol';

const Agent: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email');

  const {
    data: agentsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = fetchAgentList();
  const { data: brandsData, refetch: refetchBrandList } = fetchBrandList();
  const { data: agentBrandsData, refetch: refetchAgentBrandList } =
    fetchAgentBrandList();

  const [selectedAgent, setSelectedAgent] = useState<IAgent | null>(null);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState<
    'assigned' | 'unassigned'
  >('assigned');

  const agentBrandIds = useMemo(
    () => agentBrandsData?.agentBrands?.map((ab) => ab.agentId) || [],
    [agentBrandsData]
  );

  const filteredAgents = useMemo(() => {
    if (!agentsData?.agents) return [];
    let agentData = agentsData.agents.filter((agent) => {
      const hasAgentBrandEntry = agentBrandIds.includes(agent._id || '');

      if (assignmentFilter === 'assigned') {
        if (!hasAgentBrandEntry) return false;

        const isInBrandAgentsList = brandsData?.brands?.some((brand) =>
          brand.agents?.includes(agent.kimId || '')
        );

        return isInBrandAgentsList;
      } else {
        const isInBrandAgentsList = brandsData?.brands?.some((brand) =>
          brand.agents?.includes(agent.kimId || '')
        );

        return !isInBrandAgentsList;
      }
    });

    if (email) {
      return agentsData.agents.filter((a) => a.personalEmail === email);
    }

    return agentData;
  }, [agentsData?.agents, agentBrandIds, brandsData, assignmentFilter, email]);

  const handleOpenAgentModal = (agent: IAgent | null = null) => {
    setSelectedAgent(agent);
    setIsAgentModalOpen(true);
  };

  const handleCloseAgentModal = () => {
    setSelectedAgent(null);
    setIsAgentModalOpen(false);
  };

  const handleOpenPasswordModal = (agent: IAgent) => {
    setSelectedAgent(agent);
    setIsChangePasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setSelectedAgent(null);
    setIsChangePasswordModalOpen(false);
  };

  const handleAddEditSuccess = () => {
    toast.success(`Agent ${selectedAgent ? 'updated' : 'added'} successfully`);
    handleCloseAgentModal();
    refetch();
    refetchBrandList();
    refetchAgentBrandList();
  };

  const handlePasswordChangeSuccess = () => {
    toast.success('Password changed successfully');
    handleClosePasswordModal();
  };

  const handleWorkLogs = (agent: IAgent) =>
    navigate(`/agents/${agent.kimId}/invoice`);

  const columns = useAgentColumns({
    onView: (agent) => navigate(`/agents/${agent._id}`),
    onEdit: handleOpenAgentModal,
    onChangePassword: handleOpenPasswordModal,
    onWorkLogs: handleWorkLogs,
    brandsData: brandsData?.brands || [],
  });

  return (
    <div className="w-full h-full">
      <TableWithSearchCTA
        rowData={filteredAgents}
        columnDefs={columns}
        ctas={{
          enableExportToCsv: true,
          custom: (
            <BtnPrimary onClick={() => handleOpenAgentModal()}>
              + Add agent
            </BtnPrimary>
          ),
        }}
        headerTitle="Agents"
        description="Manage and organize agents"
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error}
        refetch={refetch}
        enableRefresh
        storageKey="agents_table"
        showAssignmentFilter
        onAssignmentSelect={setAssignmentFilter}
        selectedAssignment={assignmentFilter}
        initialSearch={email || ''}
      />

      {/* Add/Edit Agent Modal */}
      <Modal
        isOpen={isAgentModalOpen}
        onRequestClose={handleCloseAgentModal}
        title={selectedAgent ? 'Edit Agent' : 'Add Agent'}
        width={selectedAgent ? '65%' : '40%'}
      >
        <AddAgent
          initialData={selectedAgent || undefined}
          agentBrands={agentBrandsData?.agentBrands || []}
          onSuccess={handleAddEditSuccess}
          onError={(e) => toast.error(e.message)}
          isEditing={!!selectedAgent}
        />
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={isChangePasswordModalOpen}
        onRequestClose={handleClosePasswordModal}
        title="Change Agent Password"
        width="35%"
      >
        <ChangePassword
          agentId={selectedAgent?._id || ''}
          brandsData={brandsData?.brands || []}
          agentBrands={agentBrandsData?.agentBrands || []}
          onSuccess={handlePasswordChangeSuccess}
          onError={(e) => toast.error(e.message)}
        />
      </Modal>
    </div>
  );
};

export default Agent;
